import { NextPage } from 'next';
import React from 'react';
import {
    ListingContactPageProps,
    ListingContactPageWrapperProps
} from './ListingContactPage.types';
import graphQLWrapper from 'shared/graphql/withApollo';
import { useQuery } from '@apollo/client';
import { Heading1 } from '@blueprint/heading';
import Error from 'shared/components/Layout/Error';
import EmailAgentForm from 'listing-details/components/EmailAgentForm/EmailAgentForm';
import {
    LISTING_CONTACT_QUERY,
    LISTING_CONTACT_SHOW_CAPTCHA_QUERY
} from './ListingContactPage.queries';
import {
    ContactPageContainer,
    ContactPrimary,
    Layout,
    NavigationLink
} from './ListingContactPage.styles';
import Link from 'next/link';
import { ArrowheadLeftSmall } from '@blueprint/icons';
import { trackBackToListingClick } from './ListingContactPage.tracking';
import {
    getListingPageData,
    getListingSectionFromString,
    handleCanonicalPathRedirect,
    handleListingRedirect,
    getPathForNavApi
} from 'listing-details/helpers';
import Head from 'shared/components/Head/Head';
import { NextPageContextApp } from 'listing-details/types';
import {
    CompactListingPreview,
    ListingPreview
} from 'listing-details/components/shared';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { MOBILE_BREAKPOINT } from 'listing-details/constants';
import { useListingContactPageTracking } from 'listing-details/hooks';

const ListingContactPageContent = ({
    data: { listing },
    listingId,
    searchIdentifier
}: ListingContactPageProps) => {
    const {
        analyticsEcommerce,
        analyticsTaxonomy,
        emailLead,
        listingUris,
        section
    } = listing;
    useListingContactPageTracking({ listing });

    const contactPageCanonical = listingUris.contact || '';
    const listingSection = getListingSectionFromString(section || 'for-sale');
    const [isMobile] = useIsWidthLessThanBreakpoint([MOBILE_BREAKPOINT]);

    const listingDetailsUrl: string | boolean = (() => {
        const url = listingUris.detail;

        if (!url) {
            return false;
        }

        if (searchIdentifier) {
            return `${url}?search_identifier=${searchIdentifier}`;
        }

        return url;
    })();

    // Must be called client-side only since it's important GraphQL receives the
    // user's IP address
    const { data } = useQuery(LISTING_CONTACT_SHOW_CAPTCHA_QUERY, {
        variables: {
            listingId
        },
        ssr: false
    });

    const showCaptcha = Boolean(data?.listing?.emailLead?.showCaptcha);

    return (
        <ContactPageContainer>
            <Head
                pageTitle="Contact form"
                pageDescription={''}
                canonicalPath={contactPageCanonical}
                index={() => false}
            />
            <Layout id="main-content">
                {listingDetailsUrl && (
                    <Link href={listingDetailsUrl} passHref>
                        <NavigationLink
                            variant="tertiary"
                            onClick={trackBackToListingClick}
                        >
                            <ArrowheadLeftSmall />
                            Back to property details
                        </NavigationLink>
                    </Link>
                )}
                <ContactPrimary>
                    <Heading1 size="6">Email agent</Heading1>
                    {isMobile && (
                        <CompactListingPreview
                            listing={listing}
                            detailsUri={listingDetailsUrl}
                        />
                    )}
                    <EmailAgentForm
                        formFields={emailLead?.formFields}
                        section={listingSection}
                        listingId={listingId}
                        showCaptcha={showCaptcha}
                        successUri={listingUris.success}
                        searchIdentifier={searchIdentifier}
                        analytics={{
                            analyticsEcommerce,
                            analyticsTaxonomy
                        }}
                    />
                    {!isMobile && (
                        <ListingPreview
                            listing={listing}
                            detailsUri={listingDetailsUrl}
                        />
                    )}
                </ContactPrimary>
            </Layout>
        </ContactPageContainer>
    );
};

export const ListingContactPage: NextPage<ListingContactPageWrapperProps> = ({
    errorCode,
    listingId,
    searchIdentifier,
    data
}: ListingContactPageWrapperProps) => {
    if (errorCode || !data) {
        return <Error statusCode={errorCode || 500} />;
    }

    return (
        <ListingContactPageContent
            listingId={listingId}
            data={data}
            searchIdentifier={searchIdentifier}
        />
    );
};

ListingContactPage.getInitialProps = async (ctx: NextPageContextApp) => {
    const { query, asPath, req, res } = ctx;
    const listingIdParam = query['listingId'] as string;
    const listingId = parseInt(listingIdParam, 10);
    const path = getPathForNavApi(asPath || '', listingId);
    const searchIdentifier = (query.search_identifier as string) || null;

    const data = await getListingPageData(
        ctx,
        {
            query: LISTING_CONTACT_QUERY,
            variables: {
                listingId
            }
        },
        path
    );

    if (
        data?.data.listing.emailLead?.uniqueLeadIdentifier &&
        data.data.listing.listingUris.contact
    ) {
        let contactUrl = data.data.listing.listingUris.contact;
        if (searchIdentifier) {
            contactUrl = `${contactUrl}?search_identifier=${searchIdentifier}`;
        }
        handleListingRedirect(res, contactUrl, 307);
    }

    // Redirect check is only performed on the server
    if (req && res && data.data) {
        const canonicalPath =
            data.data.listing.listingUris.contact.replace(
                '/contact/',
                '/details/contact/'
            ) || '';
        handleCanonicalPathRedirect(req, res, canonicalPath);
    }

    return {
        ...data,
        searchIdentifier
    };
};

export default graphQLWrapper(ListingContactPage);
